import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../firebase";
import { Account, BalanceSnapshot } from "../../../types/spend-tracker";
import { formatCurrency } from "../../../utils/spend-calculations";
import { useAuth } from "../../components/authContext";

interface EntryWithBalances {
  date: string;
  balances: BalanceSnapshot[];
}

const EditEntries: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<EntryWithBalances[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [history, setHistory] = useState<EntryWithBalances[][]>([]);

  const loadData = useCallback(async () => {
    if (!user) return;

    try {
      // Load accounts
      const accountsRef = collection(db, "users", user.uid, "accounts");
      const accountsSnap = await getDocs(accountsRef);
      const loadedAccounts = accountsSnap.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() } as Account)
      );
      setAccounts(
        loadedAccounts.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      );

      // Load all balances
      const balancesRef = collection(db, "users", user.uid, "balances");
      const balancesSnap = await getDocs(
        query(balancesRef, orderBy("date", "desc"))
      );

      // Group balances by date
      const balancesByDate = new Map<string, BalanceSnapshot[]>();
      balancesSnap.docs.forEach((doc) => {
        const balance = { id: doc.id, ...doc.data() } as BalanceSnapshot;
        const date = balance.date;
        if (!balancesByDate.has(date)) {
          balancesByDate.set(date, []);
        }
        balancesByDate.get(date)!.push(balance);
      });

      // Create entries from grouped balances
      const loadedEntries = Array.from(balancesByDate.entries())
        .map(([date, balances]) => ({ date, balances }))
        .sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );

      setEntries(loadedEntries);
    } catch (error) {
      console.error("Error loading data:", error);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      navigate("/login-spend");
      return;
    }
    loadData();
  }, [user, navigate, loadData]);

  const calculateNetWorth = (balances: BalanceSnapshot[]): number => {
    return balances.reduce((sum, balance) => sum + balance.balance, 0);
  };

  const handleBalanceChange = async (
    entryIndex: number,
    balanceId: string,
    newValue: string
  ) => {
    if (!user) return;

    const newEntries = [...entries];
    const backup = JSON.parse(JSON.stringify(entries));
    setHistory((prev) => [...prev, backup]);

    // Find and update the balance
    const balance = newEntries[entryIndex].balances.find(
      (b) => b.id === balanceId
    );
    if (balance) {
      balance.balance = parseFloat(newValue) || 0;

      // Update in Firestore
      const balanceRef = doc(db, "users", user.uid, "balances", balanceId);
      await updateDoc(balanceRef, { balance: balance.balance });
    }

    setEntries(newEntries);
  };

  const handleDeleteEntry = async (entryIndex: number) => {
    if (!user) return;

    const confirmDelete = window.confirm(
      `Delete entry from ${new Date(
        entries[entryIndex].date
      ).toLocaleDateString()}? This will delete all balances for this date.`
    );

    if (!confirmDelete) return;

    const entry = entries[entryIndex];

    try {
      // Delete all balances for this entry
      await Promise.all(
        entry.balances.map((balance) =>
          deleteDoc(doc(db, "users", user.uid, "balances", balance.id))
        )
      );

      const newEntries = [...entries];
      newEntries.splice(entryIndex, 1);
      setEntries(newEntries);

      alert("Entry deleted successfully");
    } catch (error) {
      console.error("Error deleting entry:", error);
      alert("Failed to delete entry");
    }
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const previous = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setEntries(previous);

    // Revert changes in Firestore
    if (user) {
      previous.forEach((entry) => {
        entry.balances.forEach((balance) => {
          const balanceRef = doc(db, "users", user.uid, "balances", balance.id);
          updateDoc(balanceRef, { balance: balance.balance });
        });
      });
    }
  };

  const getAccountById = (accountId: string): Account | undefined => {
    return accounts.find((a) => a.id === accountId);
  };

  if (!user) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p style={{ color: "#888" }}>Please log in to edit entries.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", maxWidth: "800px", margin: "0 auto" }}>
      <h2 style={{ marginBottom: "1rem" }}>Edit Entries</h2>

      <div
        style={{
          display: "flex",
          gap: "12px",
          paddingBottom: "24px",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={handleUndo}
          disabled={history.length === 0}
          style={{
            padding: "0.5rem 1rem",
            borderRadius: "0.375rem",
            backgroundColor: history.length === 0 ? "#e5e7eb" : "#6b7280",
            color: "white",
            border: "none",
            cursor: history.length === 0 ? "not-allowed" : "pointer",
          }}
        >
          ⎌ Undo
        </button>
        <button
          onClick={() => navigate("/money")}
          style={{
            padding: "0.5rem 1rem",
            borderRadius: "0.375rem",
            backgroundColor: "#2563eb",
            color: "white",
            border: "none",
            cursor: "pointer",
          }}
        >
          ← Back to Dashboard
        </button>
      </div>

      {entries.length === 0 ? (
        <div style={{ textAlign: "center", padding: "3rem 0" }}>
          <p style={{ color: "#888" }}>No entries to edit.</p>
        </div>
      ) : (
        entries.map((entry, entryIndex) => {
          const netWorth = calculateNetWorth(entry.balances);
          const cashAccounts = entry.balances.filter((b) => {
            const acc = getAccountById(b.accountId);
            return acc?.type === "cash";
          });
          const creditAccounts = entry.balances.filter((b) => {
            const acc = getAccountById(b.accountId);
            return acc?.type === "credit";
          });
          const investmentAccounts = entry.balances.filter((b) => {
            const acc = getAccountById(b.accountId);
            return acc?.type === "investment";
          });

          return (
            <div
              key={entry.date}
              style={{
                marginBottom: "2rem",
                backgroundColor: "#fff",
                borderRadius: "0.5rem",
                border: "1px solid #e5e7eb",
                padding: "1.5rem",
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                  paddingBottom: "1rem",
                  borderBottom: "2px solid #e5e7eb",
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontSize: "1.25rem" }}>
                    {new Date(entry.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </h3>
                  <p
                    style={{
                      margin: "0.25rem 0 0 0",
                      fontSize: "0.875rem",
                      color: "#6b7280",
                    }}
                  >
                    Net Worth: {formatCurrency(netWorth)}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteEntry(entryIndex)}
                  style={{
                    padding: "0.5rem",
                    borderRadius: "0.375rem",
                    backgroundColor: "#fee2e2",
                    color: "#991b1b",
                    border: "1px solid #fecaca",
                    cursor: "pointer",
                    fontSize: "1.25rem",
                  }}
                  title="Delete entire entry"
                >
                  🗑️
                </button>
              </div>

              {/* Cash Accounts */}
              {cashAccounts.length > 0 && (
                <div style={{ marginBottom: "1.5rem" }}>
                  <h4
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      marginBottom: "0.75rem",
                      color: "#10b981",
                    }}
                  >
                    💰 Checking & Savings
                  </h4>
                  {cashAccounts.map((balance) => {
                    const account = getAccountById(balance.accountId);
                    if (!account) return null;

                    return (
                      <div
                        key={balance.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                          padding: "0.75rem",
                          backgroundColor: "#f9fafb",
                          borderRadius: "0.375rem",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              fontSize: "0.875rem",
                              fontWeight: "500",
                            }}
                          >
                            {account.name}
                          </label>
                          {account.provider && (
                            <p
                              style={{
                                fontSize: "0.75rem",
                                color: "#888",
                                margin: 0,
                              }}
                            >
                              {account.provider}
                              {account.last4 && ` • ****${account.last4}`}
                            </p>
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <span
                            style={{ fontSize: "0.875rem", color: "#6b7280" }}
                          >
                            $
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            value={balance.balance}
                            onChange={(e) =>
                              handleBalanceChange(
                                entryIndex,
                                balance.id,
                                e.target.value
                              )
                            }
                            style={{
                              width: "140px",
                              padding: "0.5rem",
                              borderRadius: "0.375rem",
                              border: "1px solid #d1d5db",
                              textAlign: "right",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Credit Cards */}
              {creditAccounts.length > 0 && (
                <div style={{ marginBottom: "1.5rem" }}>
                  <h4
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      marginBottom: "0.75rem",
                      color: "#ef4444",
                    }}
                  >
                    💳 Credit Cards
                  </h4>
                  {creditAccounts.map((balance) => {
                    const account = getAccountById(balance.accountId);
                    if (!account) return null;

                    return (
                      <div
                        key={balance.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                          padding: "0.75rem",
                          backgroundColor: "#f9fafb",
                          borderRadius: "0.375rem",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              fontSize: "0.875rem",
                              fontWeight: "500",
                            }}
                          >
                            {account.name}
                          </label>
                          {account.provider && (
                            <p
                              style={{
                                fontSize: "0.75rem",
                                color: "#888",
                                margin: 0,
                              }}
                            >
                              {account.provider}
                              {account.last4 && ` • ****${account.last4}`}
                            </p>
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <span
                            style={{ fontSize: "0.875rem", color: "#6b7280" }}
                          >
                            $
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            value={balance.balance}
                            onChange={(e) =>
                              handleBalanceChange(
                                entryIndex,
                                balance.id,
                                e.target.value
                              )
                            }
                            style={{
                              width: "140px",
                              padding: "0.5rem",
                              borderRadius: "0.375rem",
                              border: "1px solid #d1d5db",
                              textAlign: "right",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Investments */}
              {investmentAccounts.length > 0 && (
                <div>
                  <h4
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      marginBottom: "0.75rem",
                      color: "#2563eb",
                    }}
                  >
                    📈 Investments
                  </h4>
                  {investmentAccounts.map((balance) => {
                    const account = getAccountById(balance.accountId);
                    if (!account) return null;

                    return (
                      <div
                        key={balance.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                          padding: "0.75rem",
                          backgroundColor: "#f9fafb",
                          borderRadius: "0.375rem",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              fontSize: "0.875rem",
                              fontWeight: "500",
                            }}
                          >
                            {account.name}
                          </label>
                          {account.provider && (
                            <p
                              style={{
                                fontSize: "0.75rem",
                                color: "#888",
                                margin: 0,
                              }}
                            >
                              {account.provider}
                              {account.last4 && ` • ****${account.last4}`}
                            </p>
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <span
                            style={{ fontSize: "0.875rem", color: "#6b7280" }}
                          >
                            $
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            value={balance.balance}
                            onChange={(e) =>
                              handleBalanceChange(
                                entryIndex,
                                balance.id,
                                e.target.value
                              )
                            }
                            style={{
                              width: "140px",
                              padding: "0.5rem",
                              borderRadius: "0.375rem",
                              border: "1px solid #d1d5db",
                              textAlign: "right",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default EditEntries;
